import type { ProductDrawerViewModel } from "shared-ui/src/components/ecommerce/product-detail-drawer/types";
import { useProductDetailDrawer } from "shared-ui/src/components/ecommerce/hooks/useProductDetailDrawer";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import useGetOrderProductDetailDrawer from "@features/orders-features/hooks/useGetOrderProductDetailDrawer";
import {
  ProductDetailDrawerDto,
  ProductSpecificationDto,
  ProductDetailDrawerRequestDto,
} from "@lib/ShiOneClient";
import { useGetOrderProductDetailDrawerImages } from "@features/orders-features/hooks/useGetOrderProductDetailDrawerImages";
import { preserveSavedLinePrice } from "@features/ecommerce/helpers/preserveSavedLinePrice";

type BasicOrderProduct = {
  productName?: string | null;
  mfrPartNum?: string | null;
  productDescriptor?: string | null;
  price?: number | null;
};

export interface UseOrderProductDetailDrawerArgs {
  product: BasicOrderProduct | null;
  request: ProductDetailDrawerRequestDto | null;
}

export interface UseOrderProductDetailDrawerResult {
  isLoading: boolean;
  isError: boolean;
  /** Whether the catalog returned a record for the product. */
  hasProductDetail: boolean;
  isLoadingFallbackImages: boolean;
  enrichedProduct: ProductDrawerViewModel | null;
  liveCatalogPrice: number | null | undefined;
  images: {
    url: string;
    alt: string;
  }[];
  specification:
    | NonNullable<
        ReturnType<typeof useGetOrderProductDetailDrawer>["data"]
      >["specification"]
    | undefined;
  fullSpecification:
    | NonNullable<
        ReturnType<typeof useGetOrderProductDetailDrawer>["data"]
      >["fullSpecification"]
    | undefined;
  productDetailUrl: string | null;
}

export function useOrderProductDetailDrawer({
  product,
  request,
}: UseOrderProductDetailDrawerArgs): UseOrderProductDetailDrawerResult {
  const {
    isLoading,
    isError,
    hasProductDetail,
    isLoadingFallbackImages,
    enrichedProduct,
    images,
    specification,
    fullSpecification,
    productDetailUrl,
  } = useProductDetailDrawer<
    BasicOrderProduct,
    ProductDetailDrawerRequestDto,
    ProductSpecificationDto,
    ProductDetailDrawerDto
  >({
    product,
    request,
    shiComBaseUrl: getShiComBaseUrl(),
    useGetProductDetailDrawer: useGetOrderProductDetailDrawer,
    useGetProductDetailDrawerImages: useGetOrderProductDetailDrawerImages,
  });

  return {
    isLoading,
    isError,
    hasProductDetail,
    isLoadingFallbackImages,
    enrichedProduct: preserveSavedLinePrice(enrichedProduct, product?.price),
    liveCatalogPrice: enrichedProduct?.price,
    images,
    specification,
    fullSpecification,
    productDetailUrl,
  };
}
